import { AuditLog, AuditLogEventType, AuditLogEventTypes, AuditLogFieldLabels, AuditLogFilters } from "../api/AuditLog";
import { DefinedPropertyObject, IdTypes, IEntityMinimalModel } from "../api/GenericTypes";
import { SelectFormField } from "../common/formfields/SelectFormField";
import { eventIdToInfo } from "./AuditLog";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { PersonsVirtualizedSelectForm } from "../common/forms/EntityForms/formsVirtualized/PersonsVirtualizedSelectForm";
import { EntityFilterSideBar } from "../common/sidebar/filtersidebar/EntityFilterBar";
import { SplitDatePickerFormField } from "../common/formfields/DatePickerFormField/SplitDatePickerFormField";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../api/GenericConstants";
import { useMemo } from "react";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import { defaultAuditLogsFilters } from "./AuditLogTable";
import { toValidDate } from "../common/datetime/DateTimeFormatter";

export interface AuditLogFilterBarProps extends TabStoreFilterSidebarProps<
  AuditLog,
  AuditLogFilterForm,
  AuditLogFilters
> {
  hideEntityTypeFilter?: boolean;
}

interface SelectOption<T> {
  id: string | number;
  name: string;
  value: T;
}

export interface AuditLogFilterForm {
  timestampFrom?: Date | undefined | null;
  timestampTo?: Date | undefined | null;
  eventIds?: SelectOption<AuditLogEventType>[] | undefined | null;
  userIds?: IEntityMinimalModel[] | undefined | null;
  apiKeyIds?: IEntityMinimalModel<IdTypes>[] | undefined | null;
  entityTypes?: SelectOption<GenericEntityConstantsEntities>[] | undefined | null;
}
const defaultValues: DefinedPropertyObject<AuditLogFilterForm> = {
  timestampFrom: null,
  timestampTo: null,
  eventIds: null,
  userIds: null,
  apiKeyIds: null,
  entityTypes: null,
};

const auditLogsFormToFilters = (form: AuditLogFilterForm): AuditLogFilters => {
  let filters: AuditLogFilters = {};
  filters.userIds = Array.isArray(form.userIds) && form.userIds.length > 0 ? form.userIds?.map((d) => d.id) : null;
  filters.timestampFrom = toValidDate(form.timestampFrom);
  filters.timestampTo = toValidDate(form.timestampTo);
  filters.eventIds =
    Array.isArray(form.eventIds) && form.eventIds.length > 0 ? form.eventIds.map((e) => e.value) : null;
  filters.apiKeyIds =
    Array.isArray(form.apiKeyIds) && form.apiKeyIds.length > 0 ? form.apiKeyIds.map((a) => a.id) : null;
  filters.entityTypes =
    Array.isArray(form.entityTypes) && form.entityTypes.length > 0 ? form.entityTypes.map((e) => e.value) : null;
  return filters;
};

/**
 * Converts AuditLogFilters to AuditLogFilterForm
 * @param filters AuditLogFilters
 * @returns AuditLogFilterForm
 * @author @CorradoSurmanowicz
 */
export const auditLogsFiltersToForm = (filters: AuditLogFilters): AuditLogFilterForm => {
  let form: AuditLogFilterForm = {};
  form.userIds = filters.userIds ? filters.userIds.map((id) => ({ id, name: "" })) : null;
  form.timestampFrom = filters.timestampFrom ? new Date(filters.timestampFrom) : null;
  form.timestampTo = filters.timestampTo ? new Date(filters.timestampTo) : null;
  form.eventIds = filters.eventIds
    ? filters.eventIds.map((value) => ({ id: value, name: value.toString(), value }))
    : null;
  form.apiKeyIds = filters.apiKeyIds ? filters.apiKeyIds.map((id) => ({ id, name: "" })) : null;
  form.entityTypes = filters.entityTypes
    ? filters.entityTypes.map((value) => ({ id: value, name: value.toString(), value }))
    : null;
  return form;
};

export const AuditLogFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
  hideEntityTypeFilter,
  setFilters,
}: AuditLogFilterBarProps) => {
  const eventTypeOptionsMemo = useMemo(() => {
    var options = [...AuditLogEventTypes].sort().map((t) => {
      const info = eventIdToInfo(t);
      return { id: t, name: info.label, value: t };
    });
    if (hideEntityTypeFilter) {
      options = options.filter((o) => o.id.startsWith("Entity"));
    }
    return options;
  }, [hideEntityTypeFilter]);

  const entityTypeOptionsMemo = useMemo(() => {
    return Object.keys(genericEntityConstants)
      .sort()
      .map((key) => {
        return {
          id: key,
          name: toUppercase(
            genericEntityConstants[key as GenericEntityConstantsEntities].entityConstants.entitySingular
          ),
          value: key,
        };
      });
  }, []);

  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultAuditLogsFilters}
      formToFilters={auditLogsFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
    >
      {({ control, register, onReset }) => (
        <EntityFilterSideBar onReset={onReset}>
          <SplitDatePickerFormField
            control={control}
            idStart="timestampFrom"
            idEnd="timestampTo"
            label={`${AuditLogFieldLabels.timestamp} (from - to)`}
            horizontal={false}
            showTimeSelect
          />

          <SelectFormField
            id={"eventIds"}
            label={AuditLogFieldLabels.eventId}
            {...register}
            items={eventTypeOptionsMemo}
            control={control}
            horizontal={false}
            isMulti={true}
          />

          <PersonsVirtualizedSelectForm
            id="userIds"
            label={AuditLogFieldLabels.user}
            control={control}
            isMulti
            filters={{
              sortBy: [{ property: "NAME", sortDirection: "ASC" }],
              includeSystemUsers: true,
              hasAccount: true,
              includeSoftDeleted: true,
            }}
          />

          {!hideEntityTypeFilter && (
            <SelectFormField
              id={"entityTypes"}
              label={AuditLogFieldLabels.entityType}
              {...register}
              items={entityTypeOptionsMemo}
              control={control}
              horizontal={false}
              isMulti={true}
            />
          )}
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
